<script lang="ts" setup>
import { Form, type FormSubmitEvent } from '@primevue/forms'
import { zodResolver } from '@primevue/forms/resolvers/zod'
import { useTranslation } from 'i18next-vue'
import { Button } from 'primevue'
import Message from 'primevue/message'
import { useToast } from 'primevue/usetoast'
import { ref } from 'vue'
import { z } from 'zod'

import FormInput from '@/components/ui/FormInput.vue'
import { WebsiteService } from '@/services/public.ts'
import type { IUser } from '@/types/shared.ts'

const props = defineProps<Props>()
const emit = defineEmits(['close'])
const toast = useToast()
const isSubmitting = ref<boolean>(false)
const { t } = useTranslation()

interface Props {
  user?: IUser
}

const resolver = zodResolver(
  z.object({
    nome: z.string().min(1, { message: t('common:validators.min', { min: 1 }) }),
    cognome: z.string().min(1, { message: t('common:validators.min', { min: 1 }) }),
    email: z.string().email({ message: t('common:validators.email') }),
    message: z.string().min(1, { message: t('common:validators.min', { min: 1 }) }),
  }),
)

const initialValues = ref({
  nome: props.user?.nome || '',
  cognome: props.user?.cognome || '',
  email: props.user?.email || '',
  message: '',
})

const onFormSubmit = async (event: FormSubmitEvent) => {
  if (!event.valid) return
  isSubmitting.value = true

  const values = Object.entries(event.states).reduce(
    (acc, [key, value]) => {
      acc[key as keyof typeof initialValues.value] = value.value
      return acc
    },
    {} as typeof initialValues.value,
  )

  try {
    const requestData = {
      nome: values.nome,
      cognome: values.cognome,
      email: values.email,
      messaggio: values.message,
    }
    await WebsiteService.submitHelp(requestData)

    toast.add({
      severity: 'success',
      summary: t('common:toast.success.title'),
      detail: t('common:toast.success.description'),
      life: 3000,
    })
  } catch (error: unknown) {
    console.error('Submission error:', error)

    toast.add({
      severity: 'error',
      summary: t('common:toast.error.title'),
      detail: t('common:toast.error.description'),
      life: 3000,
    })
    isSubmitting.value = false
  } finally {
    isSubmitting.value = false
    emit('close')
  }
}

const closeDialogs = () => {
  emit('close')
}
</script>

<template>
  <Form
    v-slot="$form"
    :initial-values="initialValues"
    :resolver="resolver"
    class="flex flex-col gap-6 w-full mt-4"
    @submit="onFormSubmit"
  >
    <FormInput
      :label="t('common:user.name')"
      :disabled="!!initialValues.nome"
      name="nome"
      type="text"
    />
    <FormInput
      :label="t('common:user.surname')"
      :disabled="!!initialValues.cognome"
      name="cognome"
      type="text"
    />
    <FormInput
      :label="t('common:user.email')"
      :disabled="!!initialValues.email"
      name="email"
      type="email"
    />
    <FormField class="w-full" name="message">
      <Textarea
        id="message"
        v-model="initialValues.message"
        :placeholder="t('common:help.message-placeholder')"
        class="w-full mt-1"
        cols="30"
        rows="5"
        style="resize: none"
      />
      <Message v-if="$form.message?.invalid" severity="error" size="small">
        {{ $form.message.error?.message }}
      </Message>
    </FormField>
    <div class="flex justify-end gap-2">
      <Button
        icon="pi pi-times"
        :label="t('common:cancel')"
        text
        :disabled="isSubmitting"
        @click="closeDialogs"
      />
      <Button
        icon="pi pi-check"
        :label="t('common:submit')"
        type="submit"
        :loading="isSubmitting"
      />
    </div>
  </Form>
</template>

<style lang="scss" scoped></style>
